import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Flag, ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';

const TestPage = ({ user }) => {
  const { testId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('course_id');

  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fiveMinuteWarningShown, setFiveMinuteWarningShown] = useState(false);
  const [optionMappings, setOptionMappings] = useState({}); // Store shuffle mappings per question

  useEffect(() => {
    fetchTest();
  }, [testId]);

  // Prevent accidental navigation during active test
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (timeRemaining > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e) => {
      if (timeRemaining > 0) {
        const confirmLeave = window.confirm('Are you sure you want to leave? Your test progress will be lost.');
        if (!confirmLeave) {
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    // Add listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Push current state to prevent immediate back navigation
    if (timeRemaining > 0) {
      window.history.pushState(null, '', window.location.pathname);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [timeRemaining]);

  useEffect(() => {
    if (timeRemaining > 0) {
      // Show warning at 5 minutes
      if (timeRemaining === 300 && !fiveMinuteWarningShown) {
        toast.warning('⚠️ Only 5 minutes remaining!', {
          duration: 5000,
        });
        setFiveMinuteWarningShown(true);
      }
      
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && test) {
      // Auto-submit when time runs out
      handleSubmit(true);
    }
  }, [timeRemaining]);

  // Helper function to shuffle array
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const fetchTest = async () => {
    try {
      const response = await axios.get(`/tests/${testId}/start?course_id=${courseId}`);
      setTest(response.data.test);
      
      // Shuffle options for each question on FRONTEND
      const questionsWithShuffledOptions = response.data.questions.map(question => {
        const questionType = question.question_type || 'simple';
        
        if (questionType === 'combo') {
          // For combo questions: shuffle ONLY combo_options, keep statements in order
          const comboOptions = [
            { key: 'a', text: question.combo_option_a },
            { key: 'b', text: question.combo_option_b },
            { key: 'c', text: question.combo_option_c },
            { key: 'd', text: question.combo_option_d }
          ];
          
          // Shuffle combo options
          const shuffledComboOptions = shuffleArray(comboOptions);
          
          // Create mapping: displayed position -> original position
          const mapping = {};
          shuffledComboOptions.forEach((opt, index) => {
            const displayKey = ['a', 'b', 'c', 'd'][index];
            mapping[displayKey] = opt.key;
          });
          
          // Store mapping for this question
          optionMappings[question.id] = mapping;
          
          // Return question with shuffled combo options, statements unchanged
          return {
            ...question,
            combo_option_a: shuffledComboOptions[0].text,
            combo_option_b: shuffledComboOptions[1].text,
            combo_option_c: shuffledComboOptions[2].text,
            combo_option_d: shuffledComboOptions[3].text
            // stmt_a, stmt_b, stmt_c, stmt_d remain in original order
          };
        } else {
          // Simple MCQ: shuffle options (existing logic)
          const options = [
            { key: 'a', text: question.option_a },
            { key: 'b', text: question.option_b },
            { key: 'c', text: question.option_c },
            { key: 'd', text: question.option_d }
          ];
          
          // Shuffle the options
          const shuffledOptions = shuffleArray(options);
          
          // Create mapping: displayed position -> original position
          const mapping = {};
          shuffledOptions.forEach((opt, index) => {
            const displayKey = ['a', 'b', 'c', 'd'][index];
            mapping[displayKey] = opt.key;
          });
          
          // Store mapping for this question
          optionMappings[question.id] = mapping;
          
          // Return question with shuffled options
          return {
            ...question,
            option_a: shuffledOptions[0].text,
            option_b: shuffledOptions[1].text,
            option_c: shuffledOptions[2].text,
            option_d: shuffledOptions[3].text
          };
        }
      });
      
      setQuestions(questionsWithShuffledOptions);
      setOptionMappings(optionMappings);
      setTimeRemaining(response.data.test.time_limit);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load test:', error);
      toast.error('Failed to load test');
      navigate('/dashboard');
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleMarkForReview = () => {
    const questionId = questions[currentQuestionIndex].id;
    setMarkedForReview({ ...markedForReview, [questionId]: !markedForReview[questionId] });
    toast.success(markedForReview[questionId] ? 'Unmarked for review' : 'Marked for review');
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (!autoSubmit) {
      setShowSubmitDialog(false);
    }
    
    try {
      // Convert shuffled answers back to original positions
      const originalAnswers = {};
      for (const [questionId, displayedAnswer] of Object.entries(answers)) {
        const mapping = optionMappings[questionId];
        if (mapping && displayedAnswer) {
          // Convert displayed answer to original position
          originalAnswers[questionId] = mapping[displayedAnswer];
        } else {
          originalAnswers[questionId] = displayedAnswer;
        }
      }
      
      const response = await axios.post(`/tests/${testId}/submit`, {
        answers: originalAnswers,  // Send original positions
        course_id: courseId
      });

      toast.success(autoSubmit ? 'Time up! Test auto-submitted' : 'Test submitted successfully!');
      // Replace history to prevent going back to test page
      navigate(`/result/${response.data.attempt_id}?course_id=${courseId}`, { replace: true });
    } catch (error) {
      console.error('Failed to submit test:', error);
      toast.error('Failed to submit test');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (index) => {
    const questionId = questions[index].id;
    if (markedForReview[questionId]) return 'review';
    if (answers[questionId]) return 'answered';
    return 'not-answered';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'bg-green-500 text-white';
      case 'review': return 'bg-orange-500 text-white';
      case 'not-answered': return 'bg-gray-200 text-gray-700';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  const getAnsweredCount = () => Object.keys(answers).length;
  const getReviewCount = () => Object.keys(markedForReview).filter(k => markedForReview[k]).length;
  const getNotAnsweredCount = () => questions.length - getAnsweredCount();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading test...</p>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex justify-between items-center gap-2">
          <div>
            <h1 className="text-lg font-bold text-gray-800">{test.title}</h1>
            <p className="text-sm text-gray-600">Question {currentQuestionIndex + 1} of {questions.length}</p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            timeRemaining < 300 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
          }`}>
            <Clock className="w-5 h-5" />
            <span className="font-bold text-lg">{formatTime(timeRemaining)}</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="grid lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Main Question Area */}
          <div className="lg:col-span-3 space-y-4">
            {/* Question Card */}
            <Card>
              <CardHeader className="pb-3 px-3 sm:px-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                  <CardTitle className="text-base sm:text-xl">
                    Question {currentQuestionIndex + 1}
                  </CardTitle>
                  <Button
                    variant={markedForReview[currentQuestion.id] ? "default" : "outline"}
                    size="sm"
                    onClick={handleMarkForReview}
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    {markedForReview[currentQuestion.id] ? 'Marked' : 'Mark for Review'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-3 px-3 sm:px-6">
                <div className="space-y-4 sm:space-y-6">
                  {/* Question Text */}
                  <p className="text-sm sm:text-base md:text-lg font-medium leading-relaxed">{currentQuestion.question_text}</p>
                  
                  {/* Render based on question type */}
                  {currentQuestion.question_type === 'combo' ? (
                    // Statement-Combination MCQ
                    <>
                      {/* Statements (Not shuffled) */}
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg space-y-2">
                        <p className="font-semibold text-sm sm:text-base text-blue-900 mb-2">Statements:</p>
                        {['a', 'b', 'c', 'd'].map((stmt) => {
                          const stmtText = currentQuestion[`stmt_${stmt}`];
                          if (!stmtText) return null;
                          return (
                            <div key={stmt} className="flex items-start gap-2">
                              <span className="font-bold text-blue-700 min-w-[1.5rem]">{stmt.toUpperCase()}.</span>
                              <span className="text-sm sm:text-base text-gray-800">{stmtText}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Combination Options (Shuffled) */}
                      <div className="space-y-2 sm:space-y-3">
                        <p className="font-semibold text-sm text-gray-700">Choose the correct combination:</p>
                        {['a', 'b', 'c', 'd'].map((option) => {
                          const optionText = currentQuestion[`combo_option_${option}`];
                          if (!optionText) return null;
                          const isSelected = answers[currentQuestion.id] === option;
                          
                          return (
                            <label
                              key={option}
                              className={`flex items-start gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`question-${currentQuestion.id}`}
                                value={option}
                                checked={isSelected}
                                onChange={() => handleAnswerChange(currentQuestion.id, option)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <span className="font-semibold mr-1 sm:mr-2 text-sm sm:text-base">({option})</span>
                                <span className="text-sm sm:text-base">{optionText}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    // Simple MCQ (existing behavior)
                    <div className="space-y-2 sm:space-y-3">
                      {['a', 'b', 'c', 'd'].map((option) => {
                        const optionText = currentQuestion[`option_${option}`];
                        if (!optionText) return null;
                        const isSelected = answers[currentQuestion.id] === option;
                        
                        return (
                          <label
                            key={option}
                            className={`flex items-start gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              isSelected
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${currentQuestion.id}`}
                              value={option}
                              checked={isSelected}
                              onChange={() => handleAnswerChange(currentQuestion.id, option)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <span className="font-semibold mr-1 sm:mr-2 text-sm sm:text-base">{option.toUpperCase()}.</span>
                              <span className="text-sm sm:text-base">{optionText}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center gap-2 sm:gap-4 px-1">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                className="text-xs sm:text-sm px-2 sm:px-4"
              >
                <ChevronLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Previous</span>
              </Button>

              <div className="flex gap-1 sm:gap-2">
                <Button
                  variant="outline"
                  onClick={handleNext}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className="text-xs sm:text-sm px-2 sm:px-4"
                >
                  <span className="hidden sm:inline">Save &</span> Next
                  <ChevronRight className="w-4 h-4 sm:ml-2" />
                </Button>
                
                <Button
                  variant="destructive"
                  onClick={() => setShowSubmitDialog(true)}
                  className="text-xs sm:text-sm px-2 sm:px-4"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>

          {/* Question Navigation Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader className="px-3 sm:px-6">
                <CardTitle className="text-sm sm:text-base">Question Palette</CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="space-y-4">
                  {/* Status Legend */}
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-green-500"></div>
                      <span>Answered ({getAnsweredCount()})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-gray-200"></div>
                      <span>Not Answered ({getNotAnsweredCount()})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-orange-500"></div>
                      <span>Marked for Review ({getReviewCount()})</span>
                    </div>
                  </div>

                  {/* Question Grid */}
                  <div className="grid grid-cols-5 gap-2">
                    {questions.map((q, index) => {
                      const status = getQuestionStatus(index);
                      const isCurrent = index === currentQuestionIndex;
                      
                      return (
                        <button
                          key={q.id}
                          onClick={() => setCurrentQuestionIndex(index)}
                          className={`w-full aspect-square rounded flex items-center justify-center text-sm font-semibold transition-all ${
                            getStatusColor(status)
                          } ${
                            isCurrent ? 'ring-2 ring-blue-600 ring-offset-2' : ''
                          }`}
                        >
                          {index + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Test?</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit the test? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span>Total Questions:</span>
              <span className="font-semibold">{questions.length}</span>
            </div>
            <div className="flex justify-between p-3 bg-green-50 rounded">
              <span>Answered:</span>
              <span className="font-semibold text-green-700">{getAnsweredCount()}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span>Not Answered:</span>
              <span className="font-semibold text-gray-700">{getNotAnsweredCount()}</span>
            </div>
            <div className="flex justify-between p-3 bg-orange-50 rounded">
              <span>Marked for Review:</span>
              <span className="font-semibold text-orange-700">{getReviewCount()}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit(false)}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Submit Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestPage;